// src/pages/MerchantDetail.js
// Page publique : profil d'un marchand + ses annonces
// Maxula

import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useSeo from "../hooks/useSeo";
import { FiCheckCircle, FiMapPin, FiShoppingBag, FiArrowLeft, FiShare2, FiCheck, FiFlag, FiX, FiShield } from 'react-icons/fi';
import { merchantAPI, dealAPI, chatAPI, reportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { normalizeImagePath } from '../utils/imageUtils';
import MerchantBadge from '../components/common/MerchantBadge';
import GlowButton from '../components/ui/GlowButton';
import DealCard from '../components/deals/DealCard';
import { toast } from 'react-toastify';

// Motifs de signalement proposés (sélection rapide)
const REPORT_REASONS = [
  'Contenu frauduleux',
  'Produit non conforme',
  'Comportement abusif',
  'Spam / arnaque',
  'Autre'
];

const MerchantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user;
  const [merchant, setMerchant] = useState(null);
  const [copied, setCopied] = useState(false);
  useSeo(merchant ? { title: `${merchant.businessName} | Maxula`, description: (merchant.description || `Découvrez les offres de ${merchant.businessName} sur Maxula.`).toString().slice(0, 160), image: merchant.logo ? undefined : undefined } : {});
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contact, setContact] = useState({ message: '' });
  const [sending, setSending] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportCategory, setReportCategory] = useState('');
  const [reportReason, setReportReason] = useState('');
  const [reportBusy, setReportBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const mRes = await merchantAPI.getOne(id);
        const m = mRes.data?.data || mRes.data?.merchant || null;
        if (!m) { setNotFound(true); return; }
        setMerchant(m);
        try {
          const merchantNumericId = m.id || m._id || id;
          const dRes = await dealAPI.getAll({ merchantId: merchantNumericId, limit: 50 });
          const list = dRes.data?.data || dRes.data?.deals || [];
          setDeals(Array.isArray(list) ? list : []);
        } catch (e) { setDeals([]); }
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const sendContact = async () => {
    if (!user) { navigate('/login'); return; }
    if (!contact.message.trim()) { toast.error('Écrivez votre message'); return; }
    try {
      setSending(true);
      const res = await chatAPI.start({ merchantId: merchant?.id || id, text: contact.message.trim() });
      const convId = res.data?.data?.conversation?._id || res.data?.data?.conversation?.id;
      toast.success('Message envoyé via Maxula. Le marchand vous répondra dans votre messagerie.');
      setShowContact(false);
      setContact({ message: '' });
      navigate(convId ? `/messages?c=${convId}` : '/messages');
    } catch (e) { toast.error(e?.response?.data?.message || 'Erreur lors de l\'envoi'); }
    finally { setSending(false); }
  };

  // Ouvre le modal de signalement (remplace le window.prompt natif).
  const reportMerchant = () => {
    setReportCategory('');
    setReportReason('');
    setShowReport(true);
  };

  const closeReport = () => { if (!reportBusy) setShowReport(false); };

  // Un motif est requis : soit une catégorie (hors « Autre »), soit du texte.
  const canSubmitReport = (reportCategory && reportCategory !== 'Autre') || reportReason.trim().length > 0;

  const submitReport = async () => {
    if (!canSubmitReport || reportBusy) return;
    const parts = [];
    if (reportCategory && reportCategory !== 'Autre') parts.push(reportCategory);
    if (reportReason.trim()) parts.push(reportReason.trim());
    const reason = parts.join(' — ') || reportCategory;
    setReportBusy(true);
    try {
      await reportAPI.create({ targetType: 'merchant', targetId: id, targetLabel: merchant?.businessName, reason });
      toast.success('Signalement envoyé. Merci de votre vigilance.');
      setShowReport(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors du signalement');
    } finally {
      setReportBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (notFound || !merchant) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Marchand introuvable</h1>
        <Link to="/marchands" className="text-primary-600 hover:underline">← Retour aux marchands</Link>
      </div>
    );
  }

  const shareStore = async () => {
    const url = window.location.href;
    const title = merchant?.businessName ? `${merchant.businessName} sur Maxula` : 'Boutique Maxula';
    try {
      if (navigator.share) { await navigator.share({ title, url }); return; }
    } catch (e) { return; /* partage annulé */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch (e) { window.prompt('Copiez le lien de la boutique :', url); }
  };

  const logo = merchant.logo ? normalizeImagePath(merchant.logo) : null;
  const cover = merchant.coverImage ? normalizeImagePath(merchant.coverImage) : null;
  const addr = merchant.addresses && merchant.addresses[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Link to="/marchands" className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 mb-4">
        <FiArrowLeft /> Tous les marchands
      </Link>

      {/* En-tête */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden mb-8">
        <div className="h-32 bg-gradient-to-r from-primary-600 to-blue-700"
          style={cover ? { backgroundImage: `url(${cover})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}} />
        <div className="px-6 pb-6">
          <div className="flex items-end gap-4 -mt-10">
            <div className="w-24 h-24 rounded-2xl bg-white border-4 border-white shadow flex items-center justify-center overflow-hidden">
              {logo ? (
                <img src={logo} alt={merchant.businessName}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.outerHTML = `<span class='text-4xl font-bold text-gray-300'>${(merchant.businessName || '?').charAt(0).toUpperCase()}</span>`; }}
                  className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl font-bold text-gray-300">
                  {(merchant.businessName || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="pb-1">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{merchant.businessName}</h1>
                {merchant.verified && <FiCheckCircle className="text-blue-500" title="Vérifié" />}
                <MerchantBadge tier={merchant.tier} size="lg" />
              </div>
              <div className="text-gray-500 text-sm">{merchant.category}</div>
              {typeof merchant.score === 'number' && merchant.score > 0 && (
                <div className="mt-1 inline-flex items-center gap-2 text-sm">
                  <span className="font-semibold text-amber-600">⭐ Score {merchant.score}/100</span>
                  {merchant.scoreBadge && merchant.scoreBadge !== 'none' && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs capitalize">{merchant.scoreBadge}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {merchant.description && (
            <p className="text-gray-600 mt-4">{merchant.description}</p>
          )}

          <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
            {addr?.city && <span className="inline-flex items-center gap-1"><FiMapPin /> {addr.city}</span>}
            <span className="inline-flex items-center gap-1"><FiShoppingBag /> {deals.length} offre(s)</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => setShowContact(true)} className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700">
              Contacter ce marchand
            </button>
            <GlowButton onClick={shareStore} icon={copied ? FiCheck : FiShare2} size="sm" variant="green">
              {copied ? 'Lien copié' : 'Partager'}
            </GlowButton>
            <button onClick={reportMerchant} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50">
              Signaler
            </button>
          </div>
        </div>
      </div>

      {showContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowContact(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Contacter {merchant.businessName}</h3>
            {!user ? (
              <div className="text-sm text-gray-600">
                Connectez-vous pour contacter ce marchand via la messagerie Maxula.
              </div>
            ) : (
              <div className="space-y-2">
                <textarea value={contact.message} onChange={(e) => setContact((c) => ({ ...c, message: e.target.value }))} rows={4} placeholder="Votre message…" className="w-full border rounded-lg px-3 py-2" />
                <p className="text-[11px] text-gray-400">Envoyé via la messagerie Maxula. Le marchand vous répond ici ; vos coordonnées ne sont pas partagées.</p>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowContact(false)} className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Annuler</button>
              {user ? (
                <button onClick={sendContact} disabled={sending} className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60">
                  {sending ? 'Envoi…' : 'Envoyer'}
                </button>
              ) : (
                <button onClick={() => navigate('/login')} className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700">Se connecter</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeReport}
          role="presentation"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête */}
            <div className="flex items-start gap-3 p-5 border-b border-gray-100">
              <span className="shrink-0 w-11 h-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                <FiFlag size={20} />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold text-gray-900 leading-tight">Signaler ce marchand</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {merchant.businessName} · Aidez-nous à garder Maxula sûr
                </p>
              </div>
              <button
                type="button"
                onClick={closeReport}
                aria-label="Fermer"
                className="text-gray-400 hover:text-gray-600 p-1 shrink-0"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Corps */}
            <div className="p-5 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Quel est le problème ?</p>
                <div className="flex flex-wrap gap-2">
                  {REPORT_REASONS.map((r) => {
                    const sel = reportCategory === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setReportCategory(sel ? '' : r)}
                        className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                          sel
                            ? 'bg-red-600 text-white border-red-600'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Détails {reportCategory === 'Autre' ? '' : '(facultatif)'}
                </label>
                <textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  rows={3}
                  autoFocus
                  placeholder="Décrivez le problème…"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 resize-none"
                />
              </div>

              <p className="flex items-start gap-1.5 text-[11px] text-gray-400">
                <FiShield size={12} className="shrink-0 mt-0.5" />
                <span>Votre signalement est confidentiel et examiné par l'équipe Maxula.</span>
              </p>
            </div>

            {/* Pied */}
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/60">
              <button
                type="button"
                onClick={closeReport}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={submitReport}
                disabled={reportBusy || !canSubmitReport}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiFlag size={15} /> {reportBusy ? 'Envoi…' : 'Signaler'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Annonces */}
      <h2 className="text-xl font-bold text-gray-900 mb-4">Offres de {merchant.businessName}</h2>
      {deals.length === 0 ? (
        <div className="text-center text-gray-500 py-12 border border-dashed border-gray-200 rounded-xl">
          Ce marchand n'a pas encore d'offre active.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {deals.map((d) => (
            <DealCard key={d._id} deal={d} showAction />
          ))}
        </div>
      )}
    </div>
  );
};

export default MerchantDetail;
